// Drag / tap-target feedback from ONE resolver outcome (IMPLEMENTATION §2.5 "preview on every pointer frame",
// UX §3.9): the valid-target glow kind of a cell and the preview-bubble text above the lifted item
// ("→ Lead Lump · $12", "→ Caster: Slug", "⇄", "+$12", red "✕ Caster takes Lead"). Text comes from copy.json
// (`drag.*`, `reason.*`); names from the compiled defs. PURE (no DOM).
import type { CopyData } from '../data/schema.ts';
import type { IconKey } from '../fx/icons.ts';
import type { GlowKind } from '../render/Renderer.ts';
import type { RejectReason } from '../sim/commands.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { BType, FamId, OreId, ShopId } from '../sim/gen/ids.ts';
import type { DropOutcome } from '../sim/query.ts';
import type { SimState } from '../sim/state.ts';
import { fillCopy } from '../ui/copy.ts';

/**
 * Valid-target glow of a cell for a payload (UX §3.9): merge partners gold (merge, Build merge, buy+merge),
 * insert / deliver targets mint, empty floor a faint outline; everything else stays neutral.
 */
export function glowOf(out: DropOutcome): GlowKind | null {
  switch (out.k) {
    case 'merge':
    case 'bmerge':
    case 'buyMerge':
      return 'merge';
    case 'insert':
    case 'deliver':
      return 'valid';
    case 'move':
    case 'bmove':
    case 'buyPlace':
      return 'floor';
    default:
      return null;
  }
}

export interface Names {
  item(fam: FamId, tier: number): string;
  itemValue(fam: FamId, tier: number): number;
  building(type: BType, ore: OreId | null): string;
  shop(item: ShopId): string;
  /** A family's short label ("Lead", "Slug", "Round"). */
  fam(fam: FamId): string;
}

export function namesOf(d: SimDefs): Names {
  const famDef = (fam: FamId) => d.families.find((f) => f.id === fam);
  return {
    item: (fam, tier) => famDef(fam)?.names[tier - 1] ?? `${fam} T${tier}`,
    itemValue: (fam, tier) => famDef(fam)?.values[tier - 1] ?? 0,
    building: (type, ore) =>
      type === 'deposit'
        ? (d.deposits.find((x) => x.id === ore)?.name ?? type)
        : d.buildings[type].name,
    shop: (item) => {
      const it = d.shop.find((x) => x.id === item);
      if (!it) return item;
      return it.kind === 'deposit'
        ? (d.deposits.find((x) => x.id === it.ref)?.name ?? item)
        : (d.buildings[it.ref as Exclude<BType, 'deposit'>]?.name ?? item);
    },
    fam: (fam) => {
      const f = famDef(fam);
      const first = f?.names[0] ?? fam;
      // ore families read by their material ("Lead"), others by their first tier's name ("Slug")
      return f?.kind === 'ore' ? (first.split(' ')[0] ?? first) : first;
    },
  };
}

export interface TextCtx {
  copy: CopyData;
  names: Names;
  money(n: number): string;
  s: SimState;
  /** What building `bid` takes by hand now — query.handAccepts (the sim's acceptance rules). */
  accepts(bid: number): { fams: FamId[]; any: boolean };
}

function bld(c: TextCtx, bid: number): { type: BType; ore: OreId | null; level: number } | null {
  const b = c.s.buildings.find((x) => x.id === bid);
  if (!b) return null;
  return { type: b.type, ore: b.type === 'deposit' ? b.ore : null, level: b.level };
}

const t = (c: TextCtx, key: string, vars: Record<string, string | number> = {}): string =>
  fillCopy(c.copy.drag[key] ?? `[drag.${key}]`, vars);

/** A refusal as ≤ 4 words (copy.json reason.*); `bid` = the building that refused (fills {machine}/{fam}). */
export function reasonText(reason: RejectReason, c: TextCtx, bid: number | null): string {
  const tpl = c.copy.reason[reason] ?? reason;
  if (reason !== 'notAccepted') return fillCopy(tpl);
  const b = bid === null ? null : bld(c, bid);
  if (bid === null || b === null) return t(c, 'notAcceptedHere');
  const a = c.accepts(bid);
  return fillCopy(tpl, {
    machine: c.names.building(b.type, b.ore),
    fam:
      a.any || a.fams.length === 0 ? t(c, 'takesAny') : a.fams.map((f) => c.names.fam(f)).join('/'),
  });
}

export interface PreviewText {
  text: string;
  /** Red "✕ reason" bubble. */
  bad: boolean;
  /** UX §3.9 "→ [icon] Lead Lump · $12": the result's icon, before text[at] (merges only). */
  icon?: { key: IconKey; at: number };
}

/**
 * The preview bubble for an outcome; null = no bubble (a plain move, a same-cell no-op). `payload` = the
 * dragged item (merge / insert names), `targetBid` = the building under the hotspot (reason text).
 */
export function previewText(
  out: DropOutcome,
  c: TextCtx,
  payload: { fam: FamId; tier: number } | null,
  targetBid: number | null,
): PreviewText | null {
  const itemName = payload ? c.names.item(payload.fam, payload.tier) : '';
  switch (out.k) {
    case 'none':
    case 'move':
    case 'bmove':
      return null;
    case 'merge': {
      const name = c.names.item(out.fam, out.tier);
      const text = t(c, 'merge', { name, value: c.money(c.names.itemValue(out.fam, out.tier)) });
      const at = text.indexOf(name);
      const key: IconKey = { k: 'item', fam: out.fam, tier: out.tier };
      return at < 0 ? { text, bad: false } : { text, bad: false, icon: { key, at } };
    }
    case 'swap':
      return { text: t(c, out.max ? 'maxSwap' : 'swap'), bad: false };
    case 'sell':
      return { text: t(c, 'sell', { value: c.money(out.value) }), bad: false };
    case 'insert':
    case 'deliver': {
      const b = bld(c, out.bid);
      const machine = b ? c.names.building(b.type, b.ore) : '';
      return { text: t(c, out.k, { machine, name: itemName }), bad: false };
    }
    case 'buyPlace':
      return {
        text: t(c, out.ghost ? 'buyGhost' : 'buyPlace', {
          name: c.names.shop(out.item),
          price: c.money(out.price),
        }),
        bad: false,
      };
    case 'buyMerge':
      return {
        text: t(c, 'buyMerge', {
          name: c.names.shop(out.item),
          level: out.level,
          price: c.money(out.price),
        }),
        bad: false,
      };
    case 'bmerge': {
      const b = bld(c, out.into);
      return {
        text: t(c, 'bmerge', { name: b ? c.names.building(b.type, b.ore) : '', level: out.level }),
        bad: false,
      };
    }
    case 'bswap':
      return { text: t(c, 'bswap'), bad: false };
    case 'bsell':
      return {
        text: t(c, out.ghost ? 'ghostCancel' : 'bsell', { value: c.money(out.refund) }),
        bad: false,
      };
    case 'reject':
      return { text: t(c, 'reject', { reason: reasonText(out.reason, c, targetBid) }), bad: true };
  }
}
